/**
 * GenericCarbonStrategy — /project/:id/carbon-strategy
 * Carbon pathway assessment. Actionable, not a summary.
 *
 * For each pathway:
 *   - status: Not Assigned / Pre-screened / Conditionally Viable / Not Suitable
 *   - rationale
 *   - missing requirements (live from CDE)
 *   - next action
 * Source: projectState.carbon_pathway (CDE single source)
 */
import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { RefreshCw, ArrowLeft, CheckCircle2, XCircle, ArrowRight,
  ChevronDown, ChevronUp, AlertTriangle, Layers, FileText,
  FolderOpen, DollarSign, GitBranch } from "lucide-react";
import { useProjectWorkspace } from "@/hooks/useProjectWorkspace";
import { PageHeader, SectionCard } from "@/components/ds";

// ── Pathway status logic ────────────────────────────────────────────────────
function deriveStatus(eligible, issues, creditingPathway) {
  if (creditingPathway === undefined || creditingPathway === null) {
    // No external pathway selected yet — show pre-screening result
  }
  if (eligible) return "pre_screened";
  const count = (issues || []).length;
  if (count <= 2) return "conditionally_viable";
  return "not_suitable";
}

const STATUS_CONFIG = {
  pre_screened:        { label: "Pre-screened",        color: "bg-emerald-100 text-emerald-700 border-emerald-300",  dot: "bg-emerald-500", icon: CheckCircle2 },
  conditionally_viable:{ label: "Conditionally Viable",color: "bg-amber-100   text-amber-700   border-amber-300",   dot: "bg-amber-500",   icon: AlertTriangle },
  not_suitable:        { label: "Conditions Required", color: "bg-slate-100   text-slate-500   border-slate-300",    dot: "bg-slate-400",   icon: XCircle },
  not_assigned:        { label: "Not Assigned",         color: "bg-slate-100   text-slate-500   border-slate-300",    dot: "bg-slate-300",   icon: XCircle },
};

// ── Static pathway metadata (biogas-specific) ────────────────────────────────
const PATHWAY_META = {
  insetting: {
    label: "Insetting",
    registry: "Supply chain — contractual",
    rationale_eligible: "Project emissions sit within a single organic waste supply chain. Insetting allows the project owner to claim the reduction against their own Scope 3 upstream waste emissions. No registry registration required.",
    rationale_partial:  "Insetting is available when the waste feedstock is from own operations or a contracted supply chain. Confirm feedstock supply arrangement.",
    next_action_eligible: "Formalise the supply chain claim with a contractual clause or an internal carbon accounting policy. No external registry registration required.",
    next_action_partial:  "Confirm feedstock supply type as 'own operations' or 'long-term contract'. Return to Input Data to update.",
  },
  vcm: {
    label: "VCM",
    registry: "Verra VCS / Gold Standard",
    rationale_eligible: "Annual reduction exceeds the 100 tCO₂e/yr threshold and baseline disposal practice is confirmed. Project meets pre-screening conditions for voluntary carbon market registration under AMS-III.D or equivalent biogas methodology.",
    rationale_partial:  "One or more conditions for VCM pre-screening are not yet met. Review the requirements listed below.",
    next_action_eligible: "Engage a Validation/Verification Body (VVB) to initiate the formal validation process. Select the target registry (Verra VCS or Gold Standard) and prepare the Project Design Document (PDD).",
    next_action_partial:  "Address the listed conditions. Upload evidence documents for baseline disposal confirmation. Increase data completeness before VVB engagement.",
  },
  poa: {
    label: "PoA (Programme of Activities)",
    registry: "Verra VCS PoA",
    rationale_eligible: "Project scale and data meet minimum conditions for aggregated PoA registration. PoA is well-suited for biogas projects with multiple sites or a scaling strategy.",
    rationale_partial:  "Minimum conditions for PoA pre-screening are not fully met. A positive annual reduction is required.",
    next_action_eligible: "Enable PoA registration in the Input Data page under 'Programme of Activities'. The project will be queued for the aggregation pool review.",
    next_action_partial:  "Complete required inputs and run the calculation to generate a positive annual reduction. Then return here to enable PoA registration.",
  },
  art6: {
    label: "Article 6",
    registry: "UNFCCC Art.6.4 / National DNA",
    rationale_eligible: "Project meets data and scale conditions for Article 6 pre-screening. Article 6 pathways require formal DNA engagement and host country authorization in addition to technical validation.",
    rationale_partial:  "Article 6 has the highest data completeness and evidence requirements. Multiple conditions must be met before this pathway can be considered viable.",
    next_action_eligible: "Engage the Nigerian DNA (Department of Climate Change / Federal Ministry of Environment) to initiate the PCN process. Bilateral agreement pathway is required for ITMO issuance.",
    next_action_partial:  "Achieve Advanced data completeness (≥50%) and upload critical evidence before Art.6 engagement. See Carbon Structuring for the 6-step workflow.",
  },
};

// ── Pathway card ────────────────────────────────────────────────────────────
function PathwayCard({ pathwayKey, cdePath, creditingPathway, navigate, id }) {
  const [open, setOpen] = useState(false);
  const meta    = PATHWAY_META[pathwayKey] || {};
  const eligible = cdePath?.eligible ?? false;
  const issues   = cdePath?.issues || [];
  const status   = deriveStatus(eligible, issues, creditingPathway);
  const cfg      = STATUS_CONFIG[status] || STATUS_CONFIG.not_suitable;
  const Icon     = cfg.icon;

  const rationale = eligible
    ? meta.rationale_eligible
    : meta.rationale_partial;
  const next_action = eligible
    ? meta.next_action_eligible
    : meta.next_action_partial;

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`pathway-card-${pathwayKey}`}>
      {/* Header row */}
      <button className="w-full flex items-center justify-between px-5 py-3.5 bg-white hover:bg-slate-50 text-left"
        onClick={() => setOpen(v => !v)}>
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
          <div>
            <p className="text-xs font-bold text-slate-900">{meta.label}</p>
            <p className="text-xs text-slate-400">{meta.registry}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${cfg.color}`}>
            {cfg.label}
          </span>
          {open ? <ChevronUp size={12} className="text-slate-400" /> : <ChevronDown size={12} className="text-slate-400" />}
        </div>
      </button>

      {/* Expanded content */}
      {open && (
        <div className="px-5 pb-5 pt-3 border-t border-slate-100 bg-white space-y-4">

          {/* Rationale */}
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Rationale</p>
            <p className="text-xs text-slate-600 leading-relaxed">{rationale}</p>
          </div>

          {/* Missing requirements */}
          {issues.length > 0 && (
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Missing Requirements</p>
              <div className="space-y-1.5">
                {issues.map((issue, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <XCircle size={11} className="text-slate-300 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-500">{issue}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {eligible && (
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Conditions Met</p>
              <div className="flex items-center gap-1.5 text-xs">
                <CheckCircle2 size={11} className="text-emerald-500 flex-shrink-0" />
                <span className="text-emerald-700">All pre-screening conditions met — validation pathway available</span>
              </div>
            </div>
          )}

          {/* Next action */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
            <p className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-1">Next Action</p>
            <p className="text-xs text-blue-700 leading-snug">{next_action}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function GenericCarbonStrategy() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    loading, isEmpty, carbonPathway, reduction,
    sectorLabel, org, calcVersion, protocol, methodology, journey, projectState,
    creditingPathway,
  } = useProjectWorkspace(id);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  const PATHWAY_ORDER = ["insetting", "vcm", "poa", "art6"];
  const cdePaths    = projectState?.carbon_pathway || {};
  const cdeWarnings = projectState?.overall?.warnings || [];
  const annual      = Number(reduction?.total_tco2e_yr || reduction?.tco2e_yr || 0);
  const protocolId  = protocol || methodology || "DCI-BIOGAS-V1.0";
  const creditPw    = creditingPathway !== null && creditingPathway !== undefined ? creditingPathway : "Not Assigned";

  const eligibleCount = PATHWAY_ORDER.filter(k => cdePaths[k]?.eligible).length;

  return (
    <div className="p-6 lg:p-8 min-h-screen">

      <button onClick={() => navigate(`/project/${id}/workspace`)}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-5">
        <ArrowLeft size={13} /> {sectorLabel}
      </button>

      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">{org.name}</p>
        <PageHeader title="Carbon Strategy" />
        <p className="text-xs text-slate-400 mt-0.5">
          Pathway pre-screening · {calcVersion?.label || "latest calculation"} · Protocol: {protocolId}
        </p>
      </div>

      {/* Crediting pathway status strip */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl px-5 py-3 mb-5 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-1.5">
          <GitBranch size={12} className="text-slate-400" />
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Crediting Pathway</span>
        </div>
        <span className={`text-xs font-semibold ${creditPw === "Not Assigned" ? "text-amber-600" : "text-teal-700"}`}>
          {creditPw}
        </span>
        <span className="text-xs text-slate-400 italic">
          {creditPw === "Not Assigned"
            ? "No external registry standard selected. Pre-screening results are indicative only."
            : "Selected pathway — formal validation required before credit issuance."}
        </span>
        {annual > 0 && (
          <span className="text-xs font-semibold text-slate-600 ml-auto">
            {Number(annual).toFixed(1)} tCO₂e/yr reduction · Screened against 4 pathways
          </span>
        )}
      </div>

      {isEmpty && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
          <AlertTriangle size={24} className="text-amber-400 mx-auto mb-3" />
          <p className="text-sm font-bold text-amber-800">No calculation available</p>
          <p className="text-xs text-amber-600 mb-4">Enter project inputs to generate carbon pathway assessment.</p>
          <button onClick={() => navigate(`/project/${id}/input-data`)}
            className="px-5 py-2 bg-amber-600 text-white text-xs font-bold rounded-xl hover:bg-amber-700">
            Enter Inputs
          </button>
        </div>
      )}

      {!isEmpty && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Left 2/3 — pathway cards */}
          <div className="lg:col-span-2 space-y-4">

            {/* CDE Warnings */}
            {cdeWarnings.filter(w => w.level === "critical" || w.level === "high").length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3">
                <p className="text-xs font-bold text-amber-800 mb-2 uppercase tracking-wide">Required Actions</p>
                {cdeWarnings.filter(w => w.level === "critical" || w.level === "high").map((w, i) => (
                  <div key={i} className="flex items-start gap-1.5 text-xs mb-1">
                    <AlertTriangle size={10} className={`flex-shrink-0 mt-0.5 ${w.level === "critical" ? "text-red-500" : "text-amber-500"}`} />
                    <span className={w.level === "critical" ? "text-red-700" : "text-amber-700"}>{w.message}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Pathway cards */}
            {PATHWAY_ORDER.map(key => (
              <PathwayCard
                key={key}
                pathwayKey={key}
                cdePath={cdePaths[key]}
                creditingPathway={creditingPathway}
                navigate={navigate}
                id={id}
              />
            ))}

            {/* CTA row */}
            <div className="bg-[#0F172A] rounded-xl p-5">
              <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold mb-3">Actions</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { icon: Layers,     label: "Carbon Structuring",    path: "carbon-structuring",  desc: "6-step structuring workflow" },
                  { icon: FolderOpen, label: "Review Evidence Gaps",  path: "evidence-vault",      desc: "Upload missing documents" },
                  { icon: DollarSign, label: "Financing Implications",path: "financing",           desc: "DSCR and deal structure" },
                  { icon: FileText,   label: "Generate Reports",      path: "reports",             desc: "Climate & Circularity Report + PDD" },
                ].map(({ icon: Icon, label, path, desc }) => (
                  <button key={path} onClick={() => navigate(`/project/${id}/${path}`)}
                    className="flex items-start gap-2.5 p-3 bg-white/5 hover:bg-white/10 rounded-xl text-left transition-colors">
                    <Icon size={13} className="text-teal-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs text-white font-semibold">{label}</p>
                      <p className="text-xs text-slate-500">{desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right 1/3 — summary */}
          <div className="space-y-4">

            {/* Pathway summary */}
            <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="pathway-summary">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Pre-Screening Summary</p>
              {PATHWAY_ORDER.map(key => {
                const cdePath  = cdePaths[key];
                const eligible = cdePath?.eligible ?? false;
                const issues   = cdePath?.issues || [];
                const status   = deriveStatus(eligible, issues, creditingPathway);
                const cfg      = STATUS_CONFIG[status];
                const meta     = PATHWAY_META[key];
                return (
                  <div key={key} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                    <p className="text-xs font-semibold text-slate-900">{meta?.label || key}</p>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${cfg.color}`}>{cfg.label}</span>
                  </div>
                );
              })}
            </div>

            {/* Scale summary */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Reduction Scale</p>
              {[
                ["Annual reduction",   annual > 0 ? `${Number(annual).toFixed(1)} tCO₂e/yr` : "—"],
                ["Lifetime reduction", reduction?.lifetime_tco2e > 0 ? `${Number(reduction.lifetime_tco2e).toLocaleString()} tCO₂e` : "—"],
                ["Project life",       `${reduction?.project_life_years || "—"} yr`],
                ["Pathways viable",    `${eligibleCount} of ${PATHWAY_ORDER.length}`],
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between text-xs py-1.5 border-b border-slate-50 last:border-0">
                  <span className="text-slate-500">{l}</span>
                  <span className="font-bold text-slate-700">{v}</span>
                </div>
              ))}
            </div>

            {/* Outcome summary */}
            <div className={`border-2 rounded-xl p-5 ${eligibleCount > 0 ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"}`}>
              <div className="flex items-center gap-2 mb-2">
                {eligibleCount > 0
                  ? <CheckCircle2 size={16} className="text-emerald-600" />
                  : <AlertTriangle size={16} className="text-amber-600" />}
                <p className={`text-sm font-bold ${eligibleCount > 0 ? "text-emerald-800" : "text-amber-800"}`}>
                  {eligibleCount > 0 ? `${eligibleCount} pathway${eligibleCount > 1 ? "s" : ""} pre-screened` : "Conditions required"}
                </p>
              </div>
              <p className={`text-xs mb-4 ${eligibleCount > 0 ? "text-emerald-600" : "text-amber-600"}`}>
                {eligibleCount > 0
                  ? "Pre-screening conditions met. Proceed to Carbon Structuring to initiate the formal workflow."
                  : "Address the missing requirements above before proceeding to formal structuring."}
              </p>
              <p className="text-xs text-slate-500 italic mb-3">
                Pre-screening ≠ eligibility determination. Formal eligibility requires independent VVB review.
              </p>
              <button onClick={() => navigate(`/project/${id}/carbon-structuring`)}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800">
                Open Carbon Structuring <ArrowRight size={12} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
